<script setup lang="ts">
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { db, getRoomsPathById } from '../../configs/firebase';
import { ref as dbRef, onDisconnect, remove, set } from 'firebase/database';
import { computed, onMounted, onUnmounted, Ref, ref } from 'vue';
import { useLogger } from '../../composables/useLogger';
import { Room } from '../../interfaces/Room.interface';
import { useRealtimeDatabaseObject } from '../../composables/useRealtimeDatabaseObject';
import RoomInfoTabs from '../../components/RoomInfoTabs.vue';
import ParticipantsSection from '../../components/ParticipantsSection.vue';
import SpeakingMaterial from '../../components/SpeakingMaterial.vue';
import Chat from '../../components/Chat.vue';
import RoomDetailsBanner from '../../components/RoomDetailsBanner.vue';
import {
  Language,
  TagKey,
  useTranslations,
} from '../../composables/useTranslations';
import { AlertTriangle, MicOff } from 'lucide-vue-next';
import { useState } from '../../composables/useState';
import { useLocalStream } from '../../composables/useLocalStream';
import { AVMedia } from 'vue-audio-visual';
import {
  getLiveKit,
  preloadLiveKit,
  type LivekitRoom,
} from '../../lazy/livekit';
import { RoomPeer } from '../../interfaces/Peer.interface';

// todos:
// - remove person after 5 minutes being alone in the room

const router = useRouter();
const { log } = useLogger();
const t = useTranslations();
const {
  debugMode,
  username,
  setUsername,
  usernameSet,
  audioSettingsSet,
  selectedInputDeviceId,
  selectedOutputDeviceId,
} = useState();
const roomId = router.currentRoute.value.params['roomId'];

const livekitRoom = ref<LivekitRoom | null>(null);
const participants = ref<RoomPeer[]>([]);
const isConnecting = ref(false);

log('roomId:', roomId);
if (typeof roomId !== 'string') {
  throw new Error('Room ID not found');
}
const { item: room } = useRealtimeDatabaseObject<Room>(
  getRoomsPathById(roomId),
);

const { localStream, initStream, cleanUpLocalStream, hasAudioPermission } =
  useLocalStream();

const roomIsFull = computed(() => {
  return (room.value?.participants?.length ?? 0) >= 5;
});

const debugValues = {
  room,
  localStream,
  username,
  usernameSet,
  audioSettingsSet,
  roomIsFull,
};

log('debugValues:', debugValues);

const canJoinRoom = computed(() => {
  if (
    room.value?.title &&
    localStream.value &&
    username.value &&
    username.value.length > 0 &&
    usernameSet.value &&
    audioSettingsSet.value &&
    !roomIsFull.value
  ) {
    return true;
  }
  return false;
});

const askedToJoinRoom = ref(false);
const usernameInput = ref(username.value ?? '');

const audioInputDevices: Ref<MediaDeviceInfo[]> = ref([]);
const audioOutputDevices: Ref<MediaDeviceInfo[]> = ref([]);
const selectedMicrophone: Ref<string | null> = ref(null);
const selectedSpeaker: Ref<string | null> = ref(null);

let hasLeftRoom = false;

async function getAudioDevices() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    await navigator.mediaDevices.enumerateDevices().then((devices) => {
      log('devices:', devices);
      audioInputDevices.value = devices.filter(
        (device) => device.kind === 'audioinput',
      );
      audioOutputDevices.value = devices.filter(
        (device) => device.kind === 'audiooutput',
      );

      if (audioInputDevices.value.length > 0) {
        selectedMicrophone.value = audioInputDevices.value[0]?.deviceId || null;
      }

      if (audioOutputDevices.value.length > 0) {
        selectedSpeaker.value = audioOutputDevices.value[0]?.deviceId || null;
      }

      stream.getTracks().forEach((track) => {
        track.stop();
      });
    });
  } catch (error) {
    log('error:', error);
  }
}

function setAudioDevices() {
  const selectedMicrophoneId = selectedMicrophone.value;
  const selectedSpeakerId = selectedSpeaker.value;

  if (selectedMicrophoneId) {
    if (selectedMicrophoneId !== selectedInputDeviceId.value) {
      selectedInputDeviceId.value = selectedMicrophoneId;
    }
  }

  if (selectedSpeakerId) {
    if (selectedSpeakerId !== selectedOutputDeviceId.value) {
      selectedOutputDeviceId.value = selectedSpeakerId;
    }
  }

  audioSettingsSet.value = true;
}

log('room:', room.value);

function updateUsername() {
  if (usernameInput.value.trim().length > 0) {
    if (usernameInput.value !== username.value) {
      setUsername(usernameInput.value.trim());
    }
  }
}

async function onMicrophoneChange() {
  setAudioDevices();
  // Only re-init stream if device actually changed
  if (selectedMicrophone.value) {
    cleanUpLocalStream(); // Clean up previous stream
    await initStream(selectedMicrophone.value); // Get new stream with selected device
  }
}

async function getLiveKitConnectionDetails(
  roomName: string,
  participantName: string,
) {
  const SANDBOX_ID = 'wireless-websocket-1xp4p5';
  const response = await fetch(
    'https://cloud-api.livekit.io/api/sandbox/connection-details',
    {
      method: 'POST',
      headers: {
        'X-Sandbox-ID': SANDBOX_ID,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        roomName,
        participantName,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch LiveKit token');
  }

  return await response.json();
}

async function joinLiveKitRoom() {
  if (
    !(
      typeof roomId === 'string' &&
      roomId.length > 0 &&
      username.value &&
      username.value.length > 0
    )
  ) {
    log('roomId or username not found');
    return;
  }

  const { serverUrl, participantToken } = await getLiveKitConnectionDetails(
    roomId,
    username.value,
  );

  const {
    Room: LivekitRoom,
    RoomEvent,
    LogLevel,
    setLogLevel,
  } = await getLiveKit();

  // Connect to LiveKit
  const room = new LivekitRoom({});

  if (debugMode.value) {
    setLogLevel(LogLevel.info);
  } else {
    setLogLevel(LogLevel.error);
  }

  await room.prepareConnection(serverUrl, participantToken);

  room
    .on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      log('track subscribed', track, publication, participant);
      updateParticipants();
    })
    .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      log('track unsubscribed', track, publication, participant);
      updateParticipants();
    })
    .on(RoomEvent.Disconnected, (reason) => {
      log('disconnected', reason);
      updateParticipants();
    })
    .on(RoomEvent.TrackMuted, (track, publication) => {
      log('track muted', track, publication);
      updateParticipants();
    })
    .on(RoomEvent.TrackUnmuted, (track, publication) => {
      log('track unmuted', track, publication);
      updateParticipants();
    })
    .on(RoomEvent.LocalTrackUnpublished, (track, publication) => {
      log('local track unpublished', track, publication);
      updateParticipants();
    });

  await room.connect(serverUrl, participantToken, { autoSubscribe: true });

  // Assume you have the selected deviceId from your picker
  if (selectedInputDeviceId.value) {
    try {
      await room.switchActiveDevice('audioinput', selectedInputDeviceId.value);
    } catch (error) {
      log('error switching input device:', error);
    }
  } else {
    log('no selected input device, can not switch');
  }
  if (selectedOutputDeviceId.value) {
    try {
      await room.switchActiveDevice(
        'audiooutput',
        selectedOutputDeviceId.value,
      );
    } catch (error) {
      log('error switching output device:', error);
    }
  } else {
    log('no selected output device, can not switch');
  }

  await room.localParticipant.setMicrophoneEnabled(true);

  livekitRoom.value = room;
  updateParticipants();
}

async function addFirebasePresence() {
  if (!livekitRoom.value?.localParticipant.sid) {
    log("no local participant sid, can't set firebase presence");
    return;
  }

  const participantRef = dbRef(
    db,
    `rooms/${roomId}/participants/${livekitRoom.value?.localParticipant.sid}`,
  );
  log(`Setting presence at: ${participantRef}`);
  try {
    await set(participantRef, {
      sid: livekitRoom.value?.localParticipant.sid,
      username: username.value,
      joinedAt: Date.now(),
    });
  } catch (error) {
    console.error('Error setting participant presence in Firebase:', error);
  }

  // Set up removal of the participant's presence when they disconnect
  try {
    await onDisconnect(participantRef).remove();
    log(`onDisconnect for participant ${participantRef} set up successfully.`);
  } catch (error) {
    console.error('Error setting up onDisconnect:', error);
  }
}

function updateParticipants() {
  if (!livekitRoom.value) return;
  const all = [
    livekitRoom.value.localParticipant,
    ...Array.from(livekitRoom.value.remoteParticipants.values()),
  ];

  participants.value = all.map((p) => {
    const tracks: MediaStreamTrack[] = [];
    p.audioTrackPublications.forEach((pub) => {
      if (pub.isSubscribed && pub.track) {
        tracks.push(pub.track.mediaStreamTrack);
      }
    });
    const mediaStream: MediaStream | null = tracks.length
      ? new MediaStream(tracks)
      : null;

    return {
      joinedAt: p.joinedAt ? p.joinedAt.getTime() : Date.now(),
      sid: p.sid,
      username: p.identity,
      isMuted: p.isMicrophoneEnabled === false,
      isLocal: p.isLocal,
      stream: mediaStream,
    };
  });
}

async function leaveRoom({ navigateBack = false } = {}) {
  if (hasLeftRoom) return;
  hasLeftRoom = true;

  if (livekitRoom.value?.localParticipant?.sid) {
    log(
      'deleting participant presence here:',
      `rooms/${roomId}/participants/${livekitRoom.value?.localParticipant.sid}`,
    );
    const participantRef = dbRef(
      db,
      `rooms/${roomId}/participants/${livekitRoom.value?.localParticipant.sid}`,
    );
    log(`Removing presence at: ${participantRef}`);
    try {
      await remove(participantRef);
    } catch (error) {
      console.error('Error removing presence:', error);
    }
  }

  await livekitRoom.value?.disconnect();
  cleanUpLocalStream();

  if (navigateBack) {
    if (window.history.state.back === null) {
      await router.push('/rooms');
    } else {
      router.back();
    }
  }
}

const handleSubmit = async () => {
  if (canJoinRoom.value) {
    isConnecting.value = true;
    await joinLiveKitRoom();
    await addFirebasePresence();
    askedToJoinRoom.value = true;
    isConnecting.value = false;
  } else {
    log('not allowed to join room');
  }
};

async function toggleMute() {
  const localParticipant = livekitRoom.value?.localParticipant;
  if (localParticipant) {
    await localParticipant.setMicrophoneEnabled(
      !localParticipant.isMicrophoneEnabled,
    );
  }
}

function handleBeforeUnload() {
  // Call leaveRoom without navigation, ignore returned promise
  void leaveRoom();
}

onMounted(async () => {
  log('on mounted room');
  preloadLiveKit();
  window.addEventListener('beforeunload', handleBeforeUnload);
  await initStream();
  await getAudioDevices();
  setAudioDevices();
});

onBeforeRouteLeave((_to, _from, next) => {
  if (isConnecting.value) {
    next(false); // Block navigation
  } else {
    next(); // Allow navigation
  }
});

onUnmounted(() => {
  log('on unmounted room');
  window.removeEventListener('beforeunload', handleBeforeUnload);
  void leaveRoom();
});
</script>

<template>
  <div
    v-if="
      canJoinRoom &&
      askedToJoinRoom &&
      room &&
      localStream &&
      username &&
      livekitRoom &&
      participants.length > 0
    "
    class="bg-[#f8faff] h-full flex flex-col relative"
  >
    <section
      class="container mx-auto px-4 py-6 flex w-full flex-col gap-6 lg:flex-row lg:h-full"
    >
      <RoomDetailsBanner class="block lg:hidden" />
      <!-- Left Sidebar: Participants + Room Info -->
      <div
        class="mr-6 flex-shrink-0 gap-6 flex flex-col w-full lg:w-64 lg:h-full"
      >
        <RoomInfoTabs :room="room" />
        <ParticipantsSection
          :participants="participants"
          @toggle-mute="toggleMute"
          @leave-room="() => leaveRoom({ navigateBack: true })"
        />
      </div>

      <!-- Center: Banner + Speaking Material -->
      <div class="flex flex-col gap-6">
        <RoomDetailsBanner class="hidden lg:block" />
        <SpeakingMaterial
          :language="room.language as Language"
          :difficulty="room.difficulty"
          :tags="room.tags as TagKey[]"
        />
      </div>

      <!-- Right Sidebar: Chat -->
      <div class="w-full lg:ml-6 lg:w-80 flex flex-col h-full min-w-auto">
        <Chat :messages="room.messages ?? {}" />
      </div>
    </section>
  </div>
  <div v-else>
    <!-- Waiting Room -->
    <section id="waiting-room" class="flex flex-col items-center h-full">
      <h1 class="mt-8 text-3xl md:text-4xl font-bold">
        {{ t.readyToJoin.replace('{0}', username ? ` ${username}` : '') }}
      </h1>

      <form
        class="bg-white rounded-2xl shadow-lg p-8 max-w-2xl w-full"
        @submit.prevent="handleSubmit"
      >
        <div class="flex flex-col gap-6">
          <!-- Username Section -->
          <div class="border rounded-lg p-4">
            <h2 class="text-lg font-medium mb-2">{{ t.enterYourName }}</h2>
            <input
              v-model="usernameInput"
              type="text"
              required
              :maxlength="18"
              class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              placeholder="Your name"
              @input="updateUsername"
            />
            <div class="text-sm text-gray-500 mt-1 text-right">
              {{ usernameInput ? usernameInput.length : 0 }}/18
              {{ t.maximal }}
            </div>
          </div>

          <!-- Audio Settings Section -->
          <div class="border rounded-lg p-4 flex flex-col gap-2">
            <h2 class="text-lg font-medium">{{ t.audioSettings }}</h2>
            <div
              v-if="hasAudioPermission === false"
              class="p-4 bg-red-50 border-b border-red-100"
            >
              <div class="flex items-start gap-3">
                <div
                  class="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0"
                >
                  <MicOff class="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h3 class="text-sm font-medium text-red-800">
                    {{ t.microphoneAccessRequired }}
                  </h3>
                  <p class="text-sm text-red-600 mt-1">
                    {{ t.microphoneAccessRequiredDescription }}
                  </p>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <div class="flex items-center gap-2">
                <label class="block text-sm font-medium mb-1">
                  🎤 {{ t.microphone }}
                </label>
                <AVMedia
                  v-if="localStream"
                  :media="localStream"
                  type="frequ"
                  frequ-direction="mo"
                  line-color="#dd7631"
                  :line-width="18"
                  class="h-4 w-8"
                />
              </div>
              <select
                v-model="selectedMicrophone"
                class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
                @change="onMicrophoneChange"
              >
                <option
                  v-for="device in audioInputDevices"
                  :key="device.deviceId"
                  :value="device.deviceId"
                >
                  {{
                    device.label ||
                    `${t.microphone} ${audioInputDevices.indexOf(device) + 1}`
                  }}
                </option>
              </select>
            </div>

            <div v-if="audioOutputDevices.length > 0" class="mb-4">
              <label class="block text-sm font-medium mb-1">
                🔈 {{ t.speaker }}
              </label>
              <select
                v-model="selectedSpeaker"
                class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
                @change="setAudioDevices"
              >
                <option
                  v-for="device in audioOutputDevices"
                  :key="device.deviceId"
                  :value="device.deviceId"
                >
                  {{
                    device.label ||
                    `${t.speaker} ${audioOutputDevices.indexOf(device) + 1}`
                  }}
                </option>
              </select>
            </div>
          </div>

          <!-- Join Button -->
          <button
            type="submit"
            :disabled="!canJoinRoom"
            class="w-full px-4 py-3 mt-4 bg-[#dd7631] text-white font-medium rounded-lg hover:bg-[#c56628] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="{
              'opacity-50 cursor-not-allowed animate-pulse': isConnecting,
            }"
          >
            <span class="flex items-center justify-center gap-2">
              <svg
                v-if="isConnecting"
                class="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              {{ isConnecting ? t.connecting : t.join }}
            </span>
          </button>

          <div
            v-if="roomIsFull"
            class="p-4 mt-2 bg-red-50 border border-red-100 rounded-lg text-center"
          >
            <div class="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle class="h-4 w-4 text-red-500" />
              <span class="text-red-600 font-medium">{{ t.roomIsFull }}</span>
            </div>
            <p class="text-sm text-gray-600 mb-3">
              {{ t.roomIsFullDescription }}
            </p>
            <div class="flex justify-center">
              <router-link
                to="/rooms"
                class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm transition-colors"
              >
                {{ t.browseOtherRooms }}
              </router-link>
            </div>
          </div>
        </div>
      </form>
    </section>
  </div>
</template>
